import { resultDisplayName } from '../catalog/format'
import type { CatalogResultCard } from '../catalog/types'
import type { AgentFollowUpChip } from './types'

/**
 * The sourcing agent's canned heuristic layer (UID-36, ported from the design
 * source's `agentMatch`/`agentSummary`/`agentFollows`,
 * `Fictiv Platform.dc.html` ~L5998-6039). The ticket is explicit that this
 * "prototype 'AI' is a scripted heuristic — a real search/rank service is the
 * backend follow-up" — so this module is deliberately just framing text +
 * chip metadata layered on top of REAL catalog-search results (the shortlist
 * itself comes from `useCatalogSearch`'s data path, not a canned parts list).
 * Reuses `CatalogResultCard` throughout — never a parallel Part/SKU shape.
 */

/** The three staged "thinking" steps (README §7 screen 2), reproduced
 * verbatim from the design source's `askAgent`. */
export const AGENT_THINKING_STEPS = [
  'Parsing your request',
  'Searching MISUMI catalog + your Fictiv library',
  'Ranking by stock, lead time & price',
] as const

/** The prototype's three staggered reveal delays (ms) for `stepDone` 1/2/3. */
export const AGENT_STEP_DELAYS_MS = [520, 1080, 1680] as const

/** Matches the design source's `agentMatch`, which caps the folded-in
 * shortlist at 3 rows. */
export const AGENT_SHORTLIST_SIZE = 3

/** Takes the top N catalog-search results as the agent's ranked shortlist. */
export function buildAgentShortlist(
  items: readonly CatalogResultCard[],
): readonly CatalogResultCard[] {
  return items.slice(0, AGENT_SHORTLIST_SIZE)
}

/**
 * Canned summary sentence (`agentSummary`'s shape): a true no-match gets the
 * "couldn't find a close match" copy; otherwise a stock-note + top-pick
 * callout. `top.availability.note` stands in for the prototype's hand-rolled
 * `lead` string — the real per-item lead-time/availability copy the BFF
 * already returns for every catalog card.
 */
export function agentSummary(shortlist: readonly CatalogResultCard[]): string {
  if (shortlist.length === 0) {
    return "I couldn't find a close match in the catalog for that. Try describing the material, size, or where it mounts and I'll search again."
  }
  const inStockCount = shortlist.filter((card) => card.availability.status === 'in_stock').length
  const stockNote =
    inStockCount === shortlist.length
      ? 'all in stock'
      : inStockCount > 0
        ? `${inStockCount} in stock`
        : 'made to order'
  const top = shortlist[0]
  if (!top) {
    // Unreachable: the `shortlist.length === 0` check above already
    // returned — this narrows `noUncheckedIndexedAccess`'s `| undefined`.
    throw new Error('unreachable: shortlist is non-empty here')
  }
  const topName = resultDisplayName(top.sourceable)
  return `Found ${shortlist.length} strong ${shortlist.length > 1 ? 'matches' : 'match'} — ${stockNote}. My top pick is the ${topName} (${top.availability.note}) for its balance of price and lead time. Here's the shortlist:`
}

/**
 * The four canned follow-up chips (`agentFollows`'s shape) — only ever
 * offered alongside a non-empty shortlist; a true no-match has nothing to
 * add-to-cart, filter, or compare from.
 */
export function agentFollowUps(
  shortlist: readonly CatalogResultCard[],
): readonly AgentFollowUpChip[] {
  if (shortlist.length === 0) return []
  return [
    { label: 'Add top pick to cart', kind: 'add-to-cart' },
    { label: 'Only show in-stock', kind: 'ask', ask: 'Show only the in-stock options' },
    { label: 'Find a cheaper alternative', kind: 'ask', ask: 'Find a cheaper alternative' },
    { label: 'Compare lead times', kind: 'ask', ask: 'Compare the lead times' },
  ]
}
